import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Crown, LogOut, MessageCircle, RefreshCw } from 'lucide-react';

const SubscriptionRequired = () => {
  const navigate = useNavigate();
  const { user, logout, language, API } = useContext(AuthContext);

  const t = {
    en: {
      title: 'Subscription Required',
      expired: 'Your free trial has ended',
      neverSubscribed: 'Subscribe to activate your account',
      blocked: 'Your account access has been suspended',
      message: 'Subscribe now to continue using Helply and access all features.',
      client: 'CHF 12 / year',
      professional: 'CHF 99 / year',
      subscribe: 'Subscribe Now',
      logout: 'Logout',
      support: 'Contact Support',
      refresh: 'I already paid — refresh',
      features: ['Post & browse unlimited jobs', 'Chat with clients and professionals', 'Get verified badge', 'Priority in search results'],
    },
    de: {
      title: 'Abonnement erforderlich',
      expired: 'Ihre kostenlose Testversion ist abgelaufen',
      neverSubscribed: 'Abonnieren Sie, um Ihr Konto zu aktivieren',
      blocked: 'Ihr Kontozugang wurde gesperrt',
      message: 'Abonnieren Sie jetzt, um Helply weiter zu nutzen.',
      client: 'CHF 12 / Jahr',
      professional: 'CHF 99 / Jahr',
      subscribe: 'Jetzt abonnieren',
      logout: 'Abmelden',
      support: 'Support kontaktieren',
      refresh: 'Ich habe bezahlt — aktualisieren',
      features: ['Unbegrenzte Aufträge', 'Chat mit Kunden und Fachleuten', 'Verifiziertes Abzeichen', 'Priorität in Suchergebnissen'],
    },
    it: {
      title: 'Abbonamento Richiesto',
      expired: 'Il tuo periodo di prova è terminato',
      neverSubscribed: 'Abbonati per attivare il tuo account',
      blocked: 'L\'accesso al tuo account è stato sospeso',
      message: 'Abbonati ora per continuare a usare Helply.',
      client: 'CHF 12 / anno',
      professional: 'CHF 99 / anno',
      subscribe: 'Abbonati Ora',
      logout: 'Esci',
      support: 'Contatta il Supporto',
      refresh: 'Ho già pagato — aggiorna',
      features: ['Lavori illimitati', 'Chat con clienti e professionisti', 'Badge verificato', 'Priorità nei risultati'],
    },
    fr: {
      title: 'Abonnement Requis',
      expired: 'Votre période d\'essai a expiré',
      neverSubscribed: 'Abonnez-vous pour activer votre compte',
      blocked: 'Votre accès a été suspendu',
      message: 'Abonnez-vous maintenant pour continuer à utiliser Helply.',
      client: 'CHF 12 / an',
      professional: 'CHF 99 / an',
      subscribe: 'S\'abonner Maintenant',
      logout: 'Se déconnecter',
      support: 'Contacter le Support',
      refresh: 'J\'ai déjà payé — actualiser',
      features: ['Emplois illimités', 'Chat avec clients et professionnels', 'Badge vérifié', 'Priorité dans les résultats'],
    }
  }[language] || {
    title: 'Subscription Required', expired: 'Your free trial has ended', neverSubscribed: 'Subscribe to activate your account', blocked: 'Your account access has been suspended',
    message: 'Subscribe now to continue using Helply.', client: 'CHF 12 / year', professional: 'CHF 99 / year',
    subscribe: 'Subscribe Now', logout: 'Logout', support: 'Contact Support', refresh: 'I already paid — refresh',
    features: ['Post & browse unlimited jobs', 'Chat with clients and professionals', 'Get verified badge', 'Priority in search results']
  };

  const role = user?.role || 'client';
  const isBlocked = user?.subscription_status === 'blocked';
  const isNeverSubscribed = user?.subscription_status === 'inactive';
  const price = role === 'professional' ? t.professional : t.client;

  const handleRefresh = async () => {
    try {
      const token = localStorage.getItem('helply_token');
      const res = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('helply_user', JSON.stringify(data));
        window.location.reload();
      }
    } catch (e) { window.location.reload(); }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4" data-testid="subscription-required-page">
      <div className="max-w-md w-full">

        {/* Card */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden shadow-2xl">
          {/* Top bar */}
          <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
            <Crown className="w-6 h-6 text-white" />
            <h1 className="text-white font-bold text-lg">{t.title}</h1>
          </div>

          <div className="p-6 space-y-5">
            {/* Status message */}
            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-red-400 font-semibold text-sm mb-1">
                {isBlocked ? t.blocked : isNeverSubscribed ? t.neverSubscribed : t.expired}
              </p>
              <p className="text-gray-300 text-sm">{t.message}</p>
            </div>

            {/* Price */}
            <div className="bg-red-600/10 border border-red-600/30 rounded-xl p-4 text-center">
              <p className="text-gray-400 text-xs mb-1 uppercase tracking-wide">{role === 'professional' ? 'Professional' : 'Client'} Subscription</p>
              <p className="text-white text-3xl font-bold">{price}</p>
            </div>

            {/* Features */}
            <ul className="space-y-2">
              {t.features.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                  <div className="w-4 h-4 rounded-full bg-red-600/20 border border-red-600/40 flex items-center justify-center flex-shrink-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  </div>
                  {f}
                </li>
              ))}
            </ul>

            {/* Actions */}
            <div className="space-y-2 pt-2">
              <Button
                onClick={() => navigate(`/subscription-payment${role === 'professional' ? '?billing_role=professional' : ''}`)}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-12 text-base"
                data-testid="subscribe-now-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                {t.subscribe}
              </Button>

              <Button
                variant="ghost"
                onClick={handleRefresh}
                className="w-full text-gray-400 hover:text-white hover:bg-gray-800 text-sm h-10"
                data-testid="already-paid-refresh-btn"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                {t.refresh}
              </Button>

              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/support')}
                  className="flex-1 text-gray-500 hover:text-gray-300 hover:bg-gray-800 text-xs h-9"
                  data-testid="support-btn"
                >
                  <MessageCircle className="w-3.5 h-3.5 mr-1" />
                  {t.support}
                </Button>
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="flex-1 text-gray-500 hover:text-red-400 hover:bg-gray-800 text-xs h-9"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-3.5 h-3.5 mr-1" />
                  {t.logout}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRequired;
